import {
	dxtToLocalServerTime,
	getMomentTz,
	MesfModal,
} from "@dexteel/mesf-core";
import {
	Alert,
	Button,
	CircularProgress,
	FormControlLabel,
	Stack,
	Switch,
	TextField,
	Typography,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { useQuery } from "@tanstack/react-query";
import type { Moment } from "moment";
import { useEffect } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";

import type {
	SectionSpecMetadata,
	SectionSpecPublication,
} from "@/shared/models/SectionSpec";
import { getSectionSpec } from "@/shared/repositories/SectionSpecRepository";
import { getSectionSpecEarliestPublicationMinute } from "../../models/SectionSpecPublicationTime";

const moment = getMomentTz();

interface SectionSpecPublishModalProps {
	profileSpecId: number;
	isPending: boolean;
	onClose: () => void;
	onError: (error: Error) => void;
	onPublish: (publication: SectionSpecPublication) => void;
}

interface PublicationFormValues {
	assignRevisionManually: boolean;
	revision: string;
	effectiveDate: Moment | null;
}

interface PublicationFormProps extends SectionSpecPublishModalProps {
	metadata: SectionSpecMetadata;
}

const SectionSpecPublicationForm = ({
	metadata,
	isPending,
	onClose,
	onPublish,
}: PublicationFormProps) => {
	const { control, handleSubmit, reset } = useForm<PublicationFormValues>({
		defaultValues: {
			assignRevisionManually: false,
			revision: "",
			effectiveDate: null,
		},
	});
	const assignRevisionManually = useWatch({
		control,
		name: "assignRevisionManually",
	});
	const previousEffectiveTime =
		metadata.PreviousEffectiveDate?.getTime() ?? null;
	const latestActiveInspectionTime =
		metadata.LatestInspectedAt?.getTime() ?? null;
	const minimumDate = getSectionSpecEarliestPublicationMinute(
		previousEffectiveTime,
		latestActiveInspectionTime,
	);
	const minimumDateHelperText = minimumDate
		? `Minimum: ${dxtToLocalServerTime(
				minimumDate.toDate(),
				"MM/dd/yyyy HH:mm",
			)}`
		: undefined;
	const minimumRevision = metadata.NewRevNumber;

	useEffect(() => {
		reset({
			assignRevisionManually: false,
			revision: String(minimumRevision),
			effectiveDate: moment().startOf("minute"),
		});
		return () =>
			reset({
				assignRevisionManually: false,
				revision: "",
				effectiveDate: null,
			});
	}, [minimumRevision, reset]);

	const submit = handleSubmit((values) => {
		if (!isPending && values.effectiveDate?.isValid()) {
			onPublish({
				effectiveDate: values.effectiveDate.toDate(),
				...(values.assignRevisionManually
					? { revision: Number(values.revision) }
					: {}),
			});
		}
	});

	return (
		<>
			<MesfModal.Content data-test-id="spec-publication-content" dividers>
				<Stack spacing={2} sx={{ py: 1 }}>
					<Typography>Confirm this saved Draft?</Typography>
					<Controller
						name="effectiveDate"
						control={control}
						rules={{
							validate: (value) => {
								if (!value?.isValid())
									return "A valid Effective Date is required";

								if (minimumDate && value.isBefore(minimumDate)) {
									return `Effective Date must be on or after ${dxtToLocalServerTime(
										minimumDate.toDate(),
										"MM/dd/yyyy HH:mm",
									)}`;
								}

								return true;
							},
						}}
						render={({ field, fieldState }) => (
							<DateTimePicker
								label="Effective Date"
								value={field.value}
								inputRef={field.ref}
								onChange={(value) =>
									field.onChange(value?.clone().startOf("minute") ?? null)
								}
								format="MM/DD/YYYY HH:mm"
								views={["year", "month", "day", "hours", "minutes"]}
								timeSteps={{ minutes: 1 }}
								ampm={false}
								minDateTime={minimumDate}
								disabled={isPending}
								slotProps={{
									textField: {
										inputProps: {
											"data-test-id": "spec-publication-date-input",
										},
										fullWidth: true,
										required: true,
										size: "small",
										onBlur: field.onBlur,
										error: Boolean(fieldState.error),
										helperText: [
											fieldState.error?.message,
											fieldState.error ? undefined : minimumDateHelperText,
											"Used only when confirmation creates a new revision.",
										]
											.filter(Boolean)
											.join(" "),
									},
								}}
							/>
						)}
					/>
					<Controller
						name="assignRevisionManually"
						control={control}
						render={({ field }) => (
							<FormControlLabel
								label="Assign revision manually"
								control={
									<Switch
										checked={field.value}
										disabled={isPending}
										inputRef={field.ref}
										onBlur={field.onBlur}
										onChange={(_, checked) => field.onChange(checked)}
									/>
								}
							/>
						)}
					/>
					{assignRevisionManually && (
						<Controller
							name="revision"
							control={control}
							rules={{
								required: "Revision Number is required",
								validate: {
									integer: (value) =>
										(/^\d+$/.test(value) &&
											Number.isSafeInteger(Number(value))) ||
										"Revision Number must be a whole safe integer",
									minimum: (value) =>
										Number(value) >= minimumRevision ||
										`Revision Number must be at least ${minimumRevision}`,
								},
							}}
							render={({ field, fieldState }) => (
								<TextField
									{...field}
									label="Revision Number"
									required
									fullWidth
									size="small"
									disabled={isPending}
									slotProps={{ htmlInput: { inputMode: "numeric" } }}
									error={Boolean(fieldState.error)}
									helperText={
										fieldState.error?.message ??
										`Minimum Rev: ${minimumRevision}.`
									}
								/>
							)}
						/>
					)}
				</Stack>
			</MesfModal.Content>
			<MesfModal.Actions>
				<Button
					data-test-id="cancel-spec-publication-button"
					color="inherit"
					disabled={isPending}
					onClick={onClose}
				>
					Cancel
				</Button>
				<Button
					variant="contained"
					color="success"
					disabled={isPending}
					data-test-id="confirm-spec-publication-button"
					onClick={() => void submit()}
				>
					{isPending ? "Confirming..." : "Confirm"}
				</Button>
			</MesfModal.Actions>
		</>
	);
};

export const SectionSpecPublishModal = (
	props: SectionSpecPublishModalProps,
) => {
	const query = useQuery({
		queryKey: ["section-spec-publication", props.profileSpecId],
		queryFn: ({ signal }) =>
			getSectionSpec({ profileSpecId: props.profileSpecId }, signal),
		staleTime: 0,
		cacheTime: 0,
		onError: props.onError,
	});
	const metadata = query.data?.metadata;

	return (
		<MesfModal
			open
			title="Confirm Draft"
			maxWidth="sm"
			handleClose={() => {
				if (!props.isPending) props.onClose();
			}}
		>
			{query.isLoading ? (
				<MesfModal.Content>
					<CircularProgress size={28} />
				</MesfModal.Content>
			) : query.isError || !metadata ? (
				<>
					<MesfModal.Content>
						<Alert severity="error">
							Confirmation limits could not be loaded. Close this dialog and
							reload the specification.
						</Alert>
					</MesfModal.Content>
					<MesfModal.Actions>
						<Button disabled={props.isPending} onClick={props.onClose}>
							Close
						</Button>
					</MesfModal.Actions>
				</>
			) : (
				<SectionSpecPublicationForm {...props} metadata={metadata} />
			)}
		</MesfModal>
	);
};
